import { type JobCostEstimate, type ProposalDetail, RETAIL_VARIANT_LABELS, type RetailForm, type RetailVariant, formatQuantity, parseQuantity, retailLines, retailProblems, retailTotals, roofBundles, roofFromReading } from "@nmt/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { errorMessage, get, patch, post } from "../api";
import { Alert, Button, Checkbox, Dialog, Field, Panel, PanelHeader, SelectField, TextAreaField, cx, formatCents, parseDollars } from "../components/ui";
import { useMeasurements } from "./Materials";
import { LinkDialog } from "./Proposal";
import { HowToSend, type SendHow } from "../components/email-dialog";

/** Picks the kind of retail proposal to start. */
export function NewRetailDialog({ busy, error, onPick, onClose }: { busy: boolean; error: unknown; onPick: (v: RetailVariant | "custom") => void; onClose: () => void }) {
  const choices: { v: RetailVariant | "custom"; label: string; note: string }[] = [
    { v: "retail_roof", label: RETAIL_VARIANT_LABELS.retail_roof, note: "Priced from the roof price list: squares, shingle, and add-ons." },
    { v: "custom", label: "Custom", note: "Remodels, repairs, commercial, anything else: sections of work, allowances, options, and a payment schedule." },
  ];
  return (
    <Dialog title="New proposal" onClose={onClose}>
      <div className="flex flex-col gap-2">
        {!!error && <Alert>{errorMessage(error)}</Alert>}
        {choices.map((c) => (
          <button key={c.v} disabled={busy} onClick={() => onPick(c.v)} className="rounded-[var(--radius-control)] border border-line px-4 py-3 text-left hover:border-ink-soft disabled:opacity-60">
            <div className="font-semibold">{c.label}</div>
            <div className="text-sm text-muted">{c.note}</div>
          </button>
        ))}
      </div>
    </Dialog>
  );
}

/** A text box for a number that keeps what's typed ("28.") and reports the parsed value. */
export function NumberBox({ label, value, format, parse, onValue, className, placeholder }: { label: string; value: number | null; format: (n: number) => string; parse: (t: string) => number | null; onValue: (n: number | null) => void; className?: string; placeholder?: string }) {
  const [text, setText] = useState(value === null ? "" : format(value));
  const bad = text.trim() !== "" && (parse(text) === null || Number.isNaN(parse(text)));
  return (
    <Field
      label={label}
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={text}
      error={bad ? "Check this number" : undefined}
      onChange={(e) => {
        setText(e.target.value);
        const n = e.target.value.trim() === "" ? null : parse(e.target.value);
        if (n === null || !Number.isNaN(n)) onValue(n);
      }}
    />
  );
}

const dollars = (c: number) => (c / 100).toFixed(2);

/** The rep's editor for a retail proposal: roof pricing, extra lines, live totals and margin. */
export function RetailEditor({ p, onChange }: { p: ProposalDetail; onChange: (d: ProposalDetail) => void }) {
  const list = p.retail!.priceList;
  const [form, setForm] = useState(p.form as RetailForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ d: ProposalDetail; how: SendHow } | null>(null);
  const [roofKey, setRoofKey] = useState(0);
  const measurements = useMeasurements(p.job.id);
  const report = measurements.data?.find((m) => m.status === "read" && m.reading);
  const productKey = form.roof?.productKey ?? "";
  const estimate = useQuery({
    queryKey: ["cost-estimate", p.job.id, productKey, report?.id],
    queryFn: () => get<(JobCostEstimate & { template: { id: string; name: string } | null; from: { fileName: string } }) | null>(`/api/jobs/${p.job.id}/cost-estimate?productKey=${encodeURIComponent(productKey)}`),
    enabled: !!report && !!form.roof,
  });
  const set = (patchF: Partial<RetailForm>) => setForm((f) => ({ ...f, ...patchF }));

  // Live totals with the same code the server uses.
  const live = useMemo(() => {
    try {
      const lines = retailLines(form, list);
      const totals = retailTotals(lines, form.discountCents, form.depositPercent, list.minMarginPercent);
      return { lines, totals, problems: retailProblems(form, lines, totals) };
    } catch (e) {
      const totals = retailTotals([], 0, 0, list.minMarginPercent);
      return { lines: [], totals, problems: [errorMessage(e)] };
    }
  }, [form, list]);

  const save = useMutation({ mutationFn: (f: RetailForm) => patch<ProposalDetail>(`/api/proposals/${p.id}`, { form: f }) });
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = window.setTimeout(() => save.mutate(form), 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const roof = form.roof;
  const qty = (key: string) => roof?.addOns.find((a) => a.key === key)?.quantity ?? null;
  const setQty = (key: string, n: number | null) => roof && set({ roof: { ...roof, addOns: [...roof.addOns.filter((a) => a.key !== key), ...(n ? [{ key, quantity: n }] : [])] } });

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-5">
        <Panel>
          <PanelHeader title={RETAIL_VARIANT_LABELS[form.variant]} />
          <div className="flex flex-col gap-4 px-5 py-5">
            <Field label="Title on the proposal" value={form.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} />
            <TextAreaField
              label={form.roof ? "Scope of work: your note at the top" : "Scope of work"}
              value={form.scope}
              maxLength={10_000}
              rows={form.roof ? 3 : 5}
              onChange={(e) => set({ scope: e.target.value })}
              placeholder={form.roof ? "Replace the roof with Owens Corning Duration, an upgrade from the existing 3-tab roof." : "What we'll do, in plain words."}
            />
            {form.roof && <p className="text-xs text-muted">Below your note, the proposal describes the shingle system, how we'll install it (the steps below), anything with its own explanation (a redeck), and why NMT. That wording is in Settings → Retail pricing.</p>}
          </div>
        </Panel>

        <Panel>
          <PanelHeader
            title="Roof pricing"
            description={roof ? "Priced by the bundles the roof takes: field shingles with waste, plus ridge cap and starter. Enter the measured squares (no waste), or fill them from the measurement report." : "Price a roof from the price list."}
            action={
              <Button variant="quiet" className="px-3 py-1.5" onClick={() => set({ roof: roof ? null : { squares: 0, wastePercent: 10, ridgeLf: 0, starterLf: 0, productKey: list.products[0]?.key ?? "", addOns: [] }, steps: roof ? form.steps : form.steps.length ? form.steps : [...list.roofSteps] })}>
                {roof ? "Remove roof" : "Add roof pricing"}
              </Button>
            }
          />
          {roof && (
            <div key={roofKey} className="flex flex-col gap-4 px-5 py-5">
              {report?.reading && (
                <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    Measurement report: {report.reading.totalAreaSqFt.toLocaleString("en-US")} sq ft, {report.wastePercent}% waste. Ridge {report.reading.ridgeFt} ft, hips {report.reading.hipFt} ft.
                  </span>
                  <button
                    className="font-semibold text-brand hover:underline"
                    onClick={() => {
                      const filled = roofFromReading(report.reading!, report.wastePercent, list, roof.productKey);
                      // Keep add-ons the report doesn't cover (decking, ridge vent...).
                      set({ roof: { ...filled, addOns: [...roof.addOns.filter((a) => !filled.addOns.some((f) => f.key === a.key) && !["steep8", "steep10"].includes(a.key)), ...filled.addOns] } });
                      setRoofKey((k) => k + 1);
                    }}
                  >
                    Fill from the report
                  </button>
                </div>
              )}
              <SelectField label="Shingle" value={roof.productKey} onChange={(e) => set({ roof: { ...roof, productKey: e.target.value } })}>
                {!list.products.some((x) => x.key === roof.productKey) && <option value={roof.productKey}>Pick a shingle</option>}
                {list.products.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.name} ({formatCents(x.priceCents)}/SQ)
                  </option>
                ))}
              </SelectField>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <NumberBox label="Measured squares" value={roof.squares || null} format={formatQuantity} parse={parseQuantity} onValue={(n) => set({ roof: { ...roof, squares: n ?? 0 } })} placeholder="25.75" />
                <NumberBox label="Waste %" value={roof.wastePercent} format={String} parse={(t) => (/^\d{1,3}$/.test(t.trim()) && Number(t) <= 100 ? Number(t) : Number.NaN)} onValue={(n) => set({ roof: { ...roof, wastePercent: n ?? 0 } })} placeholder="10" />
                <NumberBox label="Hips + ridges (ft)" value={roof.ridgeLf || null} format={String} parse={(t) => (/^\d{1,6}$/.test(t.trim()) ? Number(t) : Number.NaN)} onValue={(n) => set({ roof: { ...roof, ridgeLf: n ?? 0 } })} placeholder="140" />
                <NumberBox label="Eaves + rakes (ft)" value={roof.starterLf || null} format={String} parse={(t) => (/^\d{1,6}$/.test(t.trim()) ? Number(t) : Number.NaN)} onValue={(n) => set({ roof: { ...roof, starterLf: n ?? 0 } })} placeholder="255" />
              </div>
              {(() => {
                const product = list.products.find((x) => x.key === roof.productKey);
                if (!product || !roof.squares) return null;
                const b = roofBundles(roof, product);
                return (
                  <div className="rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
                    {b.field} shingle bundles ({formatQuantity(roof.squares)} SQ + {roof.wastePercent}% × {product.bundlesPerSquare}) + {b.ridge} ridge ({roof.ridgeLf} ft ÷ {product.ridgeLfPerBundle}) + {b.starter} starter ({roof.starterLf} ft ÷ {product.starterLfPerBundle}) ={" "}
                    <strong>
                      {b.total} bundles = {(b.materialSquares / 1000).toFixed(2)} squares
                    </strong>
                  </div>
                );
              })()}
              <div>
                <div className="mb-2 text-sm font-semibold text-ink-soft">Add-ons</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {list.addOns.map((a) => (
                    <div key={a.key} className="flex items-end gap-2">
                      <NumberBox
                        key={`${a.key}-${qty(a.key) === roof.squares ? "same" : ""}`}
                        className="flex-1"
                        label={`${a.name} (${formatCents(a.priceCents)}/${a.unit}${a.unit === "SQ" && a.scaled ? ", measured SQ" : ""})`}
                        value={qty(a.key)}
                        format={formatQuantity}
                        parse={parseQuantity}
                        onValue={(n) => setQty(a.key, n)}
                        placeholder={a.unit}
                      />
                      {a.unit === "SQ" && roof.squares > 0 && qty(a.key) !== roof.squares && (
                        <button className="mb-2 text-xs font-semibold text-brand hover:underline" onClick={() => setQty(a.key, roof.squares)}>
                          All {formatQuantity(roof.squares)}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHeader title="What the customer sees" description="The proposal lists what's included and the total. Show more if this customer wants the detail." />
          <div className="flex flex-col gap-4 px-5 py-5">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Checkbox label="Show quantities" checked={form.show.quantities} onChange={(v) => set({ show: { ...form.show, quantities: v } })} />
              <Checkbox label="Show unit prices" checked={form.show.unitPrices} onChange={(v) => set({ show: { ...form.show, unitPrices: v } })} />
              <Checkbox label="Show line prices" checked={form.show.linePrices} onChange={(v) => set({ show: { ...form.show, linePrices: v } })} />
            </div>
            {roof && (
              <div className="flex flex-col gap-1">
                <TextAreaField label="How we'll install it (one step per line)" rows={8} value={form.steps.join("\n")} onChange={(e) => set({ steps: e.target.value.split("\n") })} />
                <button className="self-start text-xs font-semibold text-brand hover:underline" onClick={() => set({ steps: [...list.roofSteps] })}>
                  Back to our standard steps
                </button>
              </div>
            )}
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Other lines" description="Anything not on the price list: gutters, decking, repairs, lump-sum prices. Tick Upgrade to list it under its own Upgrades heading on the proposal. Cost is optional and only used for the margin." />
          <ul>
            {form.lines.map((l, i) => (
              <li key={i} className="grid grid-cols-[1fr_120px_120px_auto] items-start gap-2 border-b border-mist px-5 py-3">
                <div className="flex flex-col gap-1.5">
                  <Field label="Description" value={l.description} maxLength={300} onChange={(e) => set({ lines: form.lines.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)) })} />
                  <Checkbox label="Upgrade (listed under Upgrades; still part of the price)" checked={!!l.upgrade} onChange={(v) => set({ lines: form.lines.map((x, k) => (k === i ? { ...x, upgrade: v } : x)) })} />
                </div>
                <NumberBox label="Price" value={l.amountCents} format={dollars} parse={parseDollars} onValue={(n) => set({ lines: form.lines.map((x, k) => (k === i ? { ...x, amountCents: n ?? 0 } : x)) })} />
                <NumberBox label="Our cost" value={l.costCents} format={dollars} parse={parseDollars} onValue={(n) => set({ lines: form.lines.map((x, k) => (k === i ? { ...x, costCents: n } : x)) })} />
                <Button variant="quiet" className="mb-0.5" aria-label="Remove line" onClick={() => set({ lines: form.lines.filter((_, k) => k !== i) })}>
                  ×
                </Button>
              </li>
            ))}
          </ul>
          <div className="px-5 py-3">
            <Button variant="secondary" className="px-3 py-1.5" onClick={() => set({ lines: [...form.lines, { description: "", amountCents: 0, costCents: null }] })}>
              Add a line
            </Button>
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Price and terms" />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <NumberBox label="Discount" value={form.discountCents || null} format={dollars} parse={parseDollars} onValue={(n) => set({ discountCents: n ?? 0 })} placeholder="0.00" />
            <NumberBox label="Deposit at signing (%)" value={form.depositPercent || null} format={String} parse={(t) => (/^\d{1,3}$/.test(t.trim()) && Number(t) <= 100 ? Number(t) : Number.NaN)} onValue={(n) => set({ depositPercent: n ?? 0 })} placeholder="0" />
            <TextAreaField className="sm:col-span-2" label="Notes on the proposal" value={form.notes} maxLength={4000} rows={3} onChange={(e) => set({ notes: e.target.value })} placeholder="Decking beyond 2 sheets is $95 per sheet." />
          </div>
        </Panel>
      </div>

      <aside className="flex flex-col gap-4 xl:sticky xl:top-4 xl:self-start">
        <Panel className="px-5 py-4 text-sm">
          {live.lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 py-0.5 text-ink-soft">
              <span className="min-w-0 truncate">{l.description}</span>
              <span>{formatCents(l.amountCents)}</span>
            </div>
          ))}
          {form.discountCents > 0 && (
            <div className="flex justify-between py-0.5 text-ink-soft">
              <span>Discount</span>
              <span>-{formatCents(form.discountCents)}</span>
            </div>
          )}
          <div className="mt-2 flex justify-between border-t border-mist pt-2 text-base font-semibold">
            <span>Total</span>
            <span>{formatCents(live.totals.totalCents)}</span>
          </div>
          {live.totals.depositCents > 0 && (
            <div className="flex justify-between py-0.5">
              <span>Deposit ({form.depositPercent}%)</span>
              <span>{formatCents(live.totals.depositCents)}</span>
            </div>
          )}
          <div className="mt-3 rounded-[var(--radius-control)] bg-mist px-3 py-2">
            <div className="flex justify-between">
              <span>Expected cost</span>
              <span>{formatCents(live.totals.costCents)}</span>
            </div>
            <div className={cx("flex justify-between font-semibold", live.totals.belowMinimum && "text-danger")}>
              <span>Margin</span>
              <span>
                {formatCents(live.totals.marginCents)}
                {live.totals.marginPercent !== null && ` (${live.totals.marginPercent}%)`}
              </span>
            </div>
            <div className="text-xs text-muted">Only you see this. Minimum {list.minMarginPercent}%. Cost from the price list's cost per square.</div>
          </div>
          {estimate.data && (
            <div className="mt-2 rounded-[var(--radius-control)] border border-line px-3 py-2">
              <div className="mb-1 text-xs font-semibold tracking-wide text-muted uppercase">From the measurement report</div>
              <div className="flex justify-between">
                <span>Materials</span>
                <span>{formatCents(estimate.data.materialsCents)}</span>
              </div>
              <div className="flex justify-between">
                <span>Labor</span>
                <span>{formatCents(estimate.data.laborCents)}</span>
              </div>
              {(() => {
                // The report covers the roof; the other lines bring their own cost.
                const extra = form.lines.reduce((t, l) => t + (l.costCents ?? 0), 0);
                const cost = estimate.data.totalCents + extra;
                const margin = live.totals.totalCents - cost;
                const pct = live.totals.totalCents ? Math.round((margin / live.totals.totalCents) * 100) : null;
                return (
                  <div className={cx("flex justify-between font-semibold", pct !== null && pct < list.minMarginPercent && "text-danger")}>
                    <span>Margin</span>
                    <span>
                      {formatCents(margin)}
                      {pct !== null && ` (${pct}%)`}
                    </span>
                  </div>
                );
              })()}
              {!!estimate.data.unpriced.length && <div className="text-xs text-muted">No cost yet for {estimate.data.unpriced.length} material{estimate.data.unpriced.length === 1 ? "" : "s"} (Settings → Materials).</div>}
              {!estimate.data.template && <div className="text-xs text-muted">No material template for this shingle yet: materials aren't counted (Settings → Materials → Templates).</div>}
            </div>
          )}
          {live.totals.belowMinimum && (
            <div className="mt-3">
              <Alert>
                Margin is under {list.minMarginPercent}%. Check the price before sending.
              </Alert>
            </div>
          )}
        </Panel>
        <Panel className="flex flex-col gap-2 px-5 py-4">
          {live.problems.map((m) => (
            <div key={m} className="text-sm text-muted">
              {m}
            </div>
          ))}
          {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          <a href={`/api/proposals/${p.id}/preview.pdf`} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-[var(--radius-control)] bg-mist px-4 py-2 text-sm font-semibold hover:bg-line">
            Preview PDF
          </a>
          <Button variant="primary" disabled={live.problems.length > 0} onClick={async () => (await save.mutateAsync(form), setSending(true))}>
            Send to customer…
          </Button>
        </Panel>
      </aside>
      {sending && <RetailSendDialog p={p} form={form} total={live.totals.totalCents} margin={live.totals.belowMinimum ? live.totals.marginPercent : null} onClose={() => setSending(false)} onSent={(d, how) => (setSending(false), setSent({ d, how }))} />}
      {sent && <LinkDialog p={sent.d} how={sent.how} onClose={() => onChange(sent.d)} />}
    </div>
  );
}

export function RetailSendDialog({ p, form, total, margin, onClose, onSent }: { p: ProposalDetail; form: Pick<RetailForm, "signerName" | "signerEmail">; total: number; margin: number | null; onClose: () => void; onSent: (d: ProposalDetail, how: SendHow) => void }) {
  const [how, setHow] = useState<SendHow>("email");
  // The customer as they are now (the form's copy is from when the proposal was started).
  const [name, setName] = useState(p.customer.name || form.signerName);
  const [email, setEmail] = useState(p.customer.email || form.signerEmail);
  const [accept, setAccept] = useState(false);
  const send = useMutation({ mutationFn: () => post<ProposalDetail>(`/api/proposals/${p.id}/send`, { signerName: name, signerEmail: email, acceptLowMargin: accept }), onSuccess: (d) => onSent(d, how) });
  return (
    <Dialog
      title="Send to customer"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} disabled={!name.trim() || (margin !== null && !accept)} onClick={() => send.mutate()}>
            Make the proposal for {formatCents(total)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        <p className="text-sm text-ink-soft">This locks the proposal, saves the PDF to the job's files, and gives you a link to send the customer. They review and sign on their phone or computer; no account needed.</p>
        <Field label="Who's signing" value={name} onChange={(e) => setName(e.target.value)} required />
        <Field label="Their email (for the record)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        {margin !== null && <Checkbox label={`Send it anyway at a ${margin}% margin`} checked={accept} onChange={setAccept} />}
        <HowToSend value={how} onChange={setHow} />
      </div>
    </Dialog>
  );
}
